import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";
import { ButtonSize } from "@/components/Button/types";
import { Toggle } from "@/components/Toggle/Toggle";

import { useAppSelector } from "@/redux/hooks";

import { useUpdateListOutreachConfigMutation } from "@/pages/Private/redux/list/list-outreach.api";
import { listOutreachConfigSelector } from "@/pages/Private/redux/list/list-outreach.slice";

import { useGetCopywritingFrameworksQuery } from "@/pages/Private/redux/copywritingFramework/copywritingFramework.api";
import { copywritingFrameworkSelector } from "@/pages/Private/redux/copywritingFramework/copywritingFramework.slice";

import { LanguageSelectDropdown } from "@/pages/Private/pages/Messages/components/GenerateBar/LanguageSelectDropdown";
import { LanguageAutoDetectRulesModal } from "@/pages/Private/pages/Messages/components/GenerateBar/modals/LanguageAutoDetectRulesModal/LanguageAutoDetectRulesModal";

import { ListSourceType } from "@/enum/list.enum";

import { formatFrameworkName } from "@/utils/formatFrameworkName";
import { classNames } from "@/utils/classNames";

import { UpdateListOutreachConfig } from "../../../schema/list-outreach-config.schema";
import { listAutoCreateAiCopy } from "../../../components/ReachOut/reachOutCampaignsEnabled";
import { copywritingCreditLabelKey } from "../utils/copywritingCreditLabel";
import { ChatFieldRow } from "./ChatFieldRow";
import { ChatSendersField } from "./ChatSendersField";
import { OutreachSection } from "./OutreachSection";
import { SenderRoutingDisclosure } from "./SenderRoutingDisclosure";

interface ChatSectionProps {
	listId: number;
	sourceType: string;
	portalContainer?: HTMLElement | null;
}

/** `chatLanguage` stores `null` for Auto; the picker's value for the same thing is `"auto"`. */
const AUTO_LANGUAGE = "auto";

/**
 * The Chat section of the Outreach step (PREC-3687 §4.3, ACs 15-17, 19, 20): the master
 * *Always create AI copy* toggle plus the settings each auto-generated draft is seeded from.
 *
 * Everything here persists and **nothing here automates** — the trigger that reads these values
 * arrives in block 7. There is deliberately no "Preferred outreach channel" control: generation
 * always runs both channels and which draft opens first is existing frontend behaviour (O2).
 */
export const ChatSection: FunctionComponent<ChatSectionProps> = ({
	listId,
	sourceType,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const tc = (key: string) => t(`list.outreach.chat.${key}`);

	const config = useAppSelector(listOutreachConfigSelector(listId));
	const [updateOutreachConfig] = useUpdateListOutreachConfigMutation();

	useGetCopywritingFrameworksQuery();
	const { frameworks } = useAppSelector(copywritingFrameworkSelector);

	const [showLanguageRules, setShowLanguageRules] = useState(false);

	const isEnabled = listAutoCreateAiCopy(sourceType, config);

	const persist = async (body: UpdateListOutreachConfig) => {
		try {
			await updateOutreachConfig({ listId, body }).unwrap();
		} catch {
			// Errors surfaced by the global RTK Query error middleware.
		}
	};

	// Auto-suggest first, then the company's own frameworks — never a hardcoded list. Disabled
	// frameworks are left out: forcing one the company has switched off would contradict the
	// setting it made in the company settings.
	const frameworkOptions = useMemo<AutoCompleteItem[]>(
		() => [
			{ id: null, title: t("list.outreach.chat.framework.auto") },
			...(frameworks ?? [])
				.filter((framework) => framework.enabled)
				.map((framework) => ({ id: framework.id, title: formatFrameworkName(framework.name) })),
		],
		[frameworks, t]
	);

	const selectedFramework = useMemo(
		() => frameworkOptions.find((option) => option.id === (config?.chatFrameworkId ?? null)),
		[frameworkOptions, config?.chatFrameworkId]
	);

	const paragraphs = (keys: string[]) => (
		<span className="flex flex-col gap-2">
			{keys.map((key) => (
				<span key={key}>{tc(key)}</span>
			))}
		</span>
	);

	return (
		<OutreachSection subtitle={tc("subtitle")} title={tc("title")}>
			<div className="mb-2 flex items-start gap-3">
				<Toggle
					className="mt-0.5"
					isChecked={isEnabled}
					testId="chat-auto-create-toggle"
					onChange={() => persist({ autoCreateAiCopy: !isEnabled })}
				/>
				<div className="min-w-0">
					<div className="flex flex-wrap items-center gap-2">
						<p className="text-sm font-semibold text-text">{tc("autoCreate.label")}</p>
						{sourceType === ListSourceType.TRACKER && (
							<span className="rounded-full bg-light-blue px-2 py-0.5 text-[10px] font-semibold text-brand-light">
								{tc("recommendedForTracker")}
							</span>
						)}
						{/* Rendered only once the config has landed: the flag is derived server-side, and
						    guessing it from the source type here would fork the billing rule (D11). */}
						{config && (
							<span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-text-meta">
								{t(copywritingCreditLabelKey(config.isCopywritingIncluded))}
							</span>
						)}
					</div>
					<p className="mt-0.5 text-xs leading-5 text-text-meta">{tc("autoCreate.description")}</p>
				</div>
			</div>

			{/* Off greys the rest of the section and blocks interaction, but changes nothing: every
			    language / framework / sender value is kept and comes back on re-enable (AC 17). */}
			<div
				aria-disabled={!isEnabled}
				className={classNames("mt-3", !isEnabled && "pointer-events-none opacity-50")}
				data-testid="chat-config-fields"
			>
				<ChatFieldRow
					description={tc("language.description")}
					label={tc("language.label")}
					tooltip={paragraphs([
						"language.tooltip.p1",
						"language.tooltip.p2",
						"language.tooltip.p3",
					])}
				>
					<LanguageSelectDropdown
						disabled={!isEnabled}
						language={config?.chatLanguage ?? AUTO_LANGUAGE}
						portalContainer={portalContainer}
						onOpenRules={() => setShowLanguageRules(true)}
						// Map at the boundary: the picker speaks `"auto"`, the column stores `null`.
						// Persisting the literal string would leave block 7's resolver treating "auto"
						// as a real ISO code.
						onSelect={(language) =>
							persist({ chatLanguage: language === AUTO_LANGUAGE ? null : language })
						}
					/>
				</ChatFieldRow>

				<ChatFieldRow
					description={tc("framework.description")}
					label={tc("framework.label")}
					tooltip={paragraphs([
						"framework.tooltip.p1",
						"framework.tooltip.p2",
						"framework.tooltip.p3",
					])}
				>
					<Dropdown
						floating
						portal
						data={frameworkOptions}
						defaultValue={selectedFramework}
						disabled={!isEnabled}
						size={ButtonSize.XS}
						testId="chat-framework-dropdown"
						handleSelect={(item) =>
							persist({ chatFrameworkId: typeof item.id === "number" ? item.id : null })
						}
					/>
				</ChatFieldRow>

				{/* The only field whose description sits above its control (the mockup's own placement):
				    its body is the sender pool *plus* the routing disclosure, so a trailing sentence
				    would land under the rules editor instead of beside the label it describes. */}
				<ChatFieldRow
					descriptionAboveControl
					description={tc("senders.description")}
					label={tc("senders.label")}
				>
					<ChatSendersField chatSenders={config?.chatSenders} listId={listId} />
					<SenderRoutingDisclosure
						instruction={config?.senderRoutingInstruction ?? ""}
						listId={listId}
						portalContainer={portalContainer}
						templateId={config?.senderRoutingTemplateId ?? null}
					/>
				</ChatFieldRow>
			</div>

			{/* Conditional: the shared Modal is `keepMounted`, so an always-mounted copy would sit in
			    the DOM — and this one fires two queries of its own while mounted. */}
			{showLanguageRules && (
				<LanguageAutoDetectRulesModal isOpened={true} onClose={() => setShowLanguageRules(false)} />
			)}
		</OutreachSection>
	);
};
